/*
 * <withdrawal-form>: two-step withdrawal request (sections/withdrawal-form.liquid).
 * Step 1 collects the data; "Continua" validates it and shows a summary with the
 * declaration; "Conferma recesso" submits Shopify's contact form. Before submitting it
 * writes the declaration (contact[body]) and the local date/time (contact[Data e ora di invio])
 * so the store's email carries both, and keeps a copy in sessionStorage to show on the
 * success screen (and to refill the form if Shopify returns an error).
 */
class WithdrawalForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.key = `withdrawal:${this.dataset.formId}`;

    const done = this.querySelector('[data-done]');
    if (done) {
      this.showDone(done);
      return;
    }
    if (!this.form) return;

    this.step1 = this.querySelector('[data-step="1"]');
    this.step2 = this.querySelector('[data-step="2"]');
    this.submitRow = this.querySelector('[data-submit-row]');
    this.items = this.querySelector('[data-items]');
    this.itemsField = this.items?.querySelector('textarea');

    this.querySelectorAll('[data-js-only]').forEach((el) => (el.hidden = false));
    this.submitRow.hidden = true;

    this.restore();
    this.syncScope();
    this.form.addEventListener('change', (event) => {
      if (event.target.matches('[data-scope]')) this.syncScope();
    });
    this.form.addEventListener('input', (event) => {
      const field = event.target;
      if (field.getAttribute('aria-invalid') === 'true' && field.checkValidity()) field.setAttribute('aria-invalid', 'false');
    });
    this.querySelector('[data-next]').addEventListener('click', () => this.review());
    this.querySelector('[data-back]').addEventListener('click', () => this.edit());
    this.form.addEventListener('submit', () => this.stamp());

    this.querySelector('[data-error]')?.focus();
  }

  get fields() {
    return Array.from(this.step1.querySelectorAll('input:not([type="hidden"]), textarea'));
  }

  syncScope() {
    const some = this.form.querySelector('[data-scope="some"]').checked;
    this.items.hidden = !some;
    this.itemsField.required = some;
  }

  values() {
    const get = (name) => (this.form.elements.namedItem(name)?.value || '').trim();
    const scope = this.form.querySelector('[data-scope]:checked')?.value || '';
    return {
      name: get('contact[name]'),
      email: get('contact[email]'),
      order: get('contact[Numero ordine]'),
      scope,
      items: scope === 'Solo alcuni articoli' ? get('contact[Articoli]') : '',
      reason: get('contact[Motivo]'),
    };
  }

  declaration(v) {
    // Wording follows the EU model withdrawal form (Codice del Consumo, allegato I parte B).
    const what = v.items ? `limitatamente ai seguenti articoli: ${v.items}` : "per tutti i prodotti dell'ordine";
    return `Con la presente io, ${v.name}, notifico il recesso dal mio contratto di vendita relativo all'ordine ${v.order}, ${what}.`;
  }

  review() {
    for (const field of this.fields) {
      field.setAttribute('aria-invalid', String(!field.checkValidity()));
    }
    if (!this.fields.every((field) => field.checkValidity())) {
      this.fields.find((field) => !field.checkValidity())?.reportValidity();
      return;
    }

    const v = this.values();
    const rows = [
      ['Nome e cognome', v.name],
      ['Email', v.email],
      ["Numero d'ordine", v.order],
      ['Prodotti', v.items ? `${v.scope}:\n${v.items}` : v.scope],
      ['Motivo', v.reason || 'Non indicato'],
    ];
    const summary = this.querySelector('[data-summary]');
    summary.replaceChildren(
      ...rows.flatMap(([label, value]) => {
        const dt = document.createElement('dt');
        dt.textContent = label;
        const dd = document.createElement('dd');
        dd.textContent = value;
        return [dt, dd];
      })
    );
    this.querySelector('[data-declaration]').textContent = this.declaration(v);

    this.step1.hidden = true;
    this.step2.hidden = false;
    this.submitRow.hidden = false;
    this.querySelector('[data-back]').hidden = false;
    this.step2.querySelector('h2').focus();
    this.scrollToTop();
  }

  edit() {
    this.step2.hidden = true;
    this.submitRow.hidden = true;
    this.step1.hidden = false;
    this.step1.querySelector('legend').focus();
    this.scrollToTop();
  }

  stamp() {
    const v = this.values();
    const sentAt = new Date().toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'medium' });
    this.form.querySelector('[data-sent-at]').value = sentAt;
    this.form.querySelector('[data-declaration-field]').value = this.declaration(v);
    try {
      sessionStorage.setItem(this.key, JSON.stringify({ ...v, sentAt, declaration: this.declaration(v) }));
    } catch (e) {
      /* storage unavailable: the success screen just shows less detail */
    }
  }

  restore() {
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.key) || 'null');
    } catch (e) {
      saved = null;
    }
    if (!saved || !this.querySelector('[data-error]')) return;
    const set = (name, value) => {
      const field = this.form.elements.namedItem(name);
      if (field && 'value' in field && !field.value) field.value = value || '';
    };
    set('contact[name]', saved.name);
    set('contact[email]', saved.email);
    set('contact[Numero ordine]', saved.order);
    set('contact[Articoli]', saved.items);
    set('contact[Motivo]', saved.reason);
    if (saved.items) this.form.querySelector('[data-scope="some"]').checked = true;
  }

  showDone(done) {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.key) || 'null');
    } catch (e) {
      saved = null;
    }
    const box = done.querySelector('[data-done-summary]');
    if (saved && box) {
      const p = document.createElement('p');
      p.textContent = saved.declaration;
      const meta = document.createElement('p');
      meta.textContent = `Email per la conferma: ${saved.email}`;
      box.replaceChildren(p, meta);
      box.hidden = false;
    }
    done.querySelector('[data-print]')?.addEventListener('click', () => window.print());
    done.focus();
  }

  scrollToTop() {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('withdrawal-form')) {
  customElements.define('withdrawal-form', WithdrawalForm);
}
